import type { ViewController } from '@presentation/shell/NexxusShell';
import type { Salarie } from '@shared/types/Salarie';
import { esc, fmtDate } from '@presentation/shell/format';
import { ToastService } from '@presentation/components/Toast';
import { confirmAction } from '@presentation/components/ConfirmDialog';
import { canMutateRecords } from '@application/access/ViewAccessPolicy';
import {
  CoffreFortStorageService,
  type CoffreFortDocument,
} from '@application/persistence/CoffreFortStorageService';

function humanSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
}

function inferCategory(name: string): string {
  const n = name.toLowerCase();
  if (n.includes('contrat')) return 'Contrats';
  if (n.includes('bulletin') || n.includes('paie')) return 'Bulletins';
  if (n.includes('attest') || n.includes('certif')) return 'Attestations';
  if (n.includes('cnss') || n.includes('cnamgs') || n.includes('dgi') || n.includes('impot'))
    return 'Déclarations';
  return 'Autres';
}

function syncLabel(doc: CoffreFortDocument): string {
  if (doc.syncStatus === 'pending_upload') return ' · en attente de sync';
  if (doc.syncStatus === 'remote') return '';
  return ' · local';
}

export const coffrefortController: ViewController = async (section, ctx, shell) => {
  (window as unknown as Record<string, unknown>)['openActivePrivacyPolicy'] = () => {
    void import('@presentation/shell/controllers/self').then(({ ensureOpenActivePrivacyPolicy }) =>
      ensureOpenActivePrivacyPolicy(ctx, section),
    );
  };
  const tenantId = ctx.tenantContext.tryGetTenant()?.id ?? 'default';
  const canMutate = canMutateRecords(shell.permission);
  const salaries = await ctx.salaries.list();
  const salarieName = (id: string | null): string => {
    if (!id) return 'Entreprise';
    const s = salaries.find((x: Salarie) => x.id === id);
    return s ? `${s.prenom} ${s.nom}` : 'Salarié';
  };

  const downloadUrls = new Map<string, string>();

  const filterSalarie = section.querySelector<HTMLSelectElement>('#cf-filter-salarie');
  if (filterSalarie && filterSalarie.options.length <= 1) {
    for (const s of salaries) {
      const o = document.createElement('option');
      o.value = s.id;
      o.textContent = `${s.prenom} ${s.nom}`;
      filterSalarie.appendChild(o);
    }
  }

  const render = async (): Promise<void> => {
    const docs = await CoffreFortStorageService.listDocuments(ctx, tenantId);
    const search = (
      section.querySelector<HTMLInputElement>('#search-docs')?.value ?? ''
    ).toLowerCase();
    const fSal = section.querySelector<HTMLSelectElement>('#cf-filter-salarie')?.value ?? '';
    const fScope = section.querySelector<HTMLSelectElement>('#cf-filter-scope')?.value ?? 'all';

    const total = docs.length;
    const size = docs.reduce((s, d) => s + d.size, 0);
    const st = section.querySelector('#cf-stat-total');
    if (st) st.textContent = String(total);
    const ss = section.querySelector('#cf-stat-size');
    const ssCard = ss?.closest('.sd-stat') as HTMLElement | null;
    if (ss) {
      if (size <= 0) {
        // BUG-25 — masquer le widget « 0 o » si stockage non implémenté / vide
        if (ssCard) ssCard.style.display = 'none';
        else ss.textContent = '—';
      } else {
        if (ssCard) ssCard.style.display = '';
        ss.textContent = humanSize(size);
      }
    }

    const filtered = docs.filter((d) => {
      if (fScope !== 'all' && d.scope !== fScope) return false;
      if (fSal && d.salarie_id !== fSal) return false;
      if (
        search &&
        !`${d.name} ${salarieName(d.salarie_id)} ${d.category}`.toLowerCase().includes(search)
      )
        return false;
      return true;
    });

    const cats = new Map<string, number>();
    for (const d of filtered) cats.set(d.category, (cats.get(d.category) ?? 0) + 1);
    const catBox = section.querySelector<HTMLElement>('#cf-categories');
    if (catBox) {
      catBox.innerHTML = [...cats.entries()]
        .map(([c, n]) => `<span class="pill" style="margin:4px 6px 4px 0;">${esc(c)} · ${n}</span>`)
        .join('');
    }

    const list = section.querySelector<HTMLElement>('#docs-list');
    if (!list) return;
    if (filtered.length === 0) {
      list.innerHTML = '<div class="empty" style="padding:24px;"><p>Aucun document.</p></div>';
      return;
    }

    for (const oldUrl of downloadUrls.values()) URL.revokeObjectURL(oldUrl);
    downloadUrls.clear();

    const rows: string[] = [];
    for (const d of filtered) {
      let href = '#';
      try {
        const blob = await CoffreFortStorageService.getDownloadBlob(d, tenantId);
        href = URL.createObjectURL(blob);
        downloadUrls.set(d.id, href);
      } catch {
        href = '#';
      }
      rows.push(`<div class="cf-doc" style="display:flex;align-items:center;justify-content:space-between;gap:12px;padding:10px 12px;border-bottom:1px solid var(--neige);">
        <div style="flex:1;min-width:0;">
          <strong style="display:block;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${esc(d.name)}</strong>
          <span style="font-size:11px;color:var(--gris);">${esc(salarieName(d.salarie_id))} · ${esc(d.category)} · ${humanSize(d.size)} · ${esc(fmtDate(d.uploadedAt))}${syncLabel(d)}</span>
        </div>
        <div style="display:flex;gap:6px;flex:0 0 auto;">
          <a class="btn btn-sm btn-secondary" href="${href}" download="${esc(d.name)}" ${href === '#' ? 'aria-disabled="true"' : ''}>Télécharger</a>
          <button class="btn btn-sm btn-danger" data-on-click="deleteCoffreDoc('${d.id}')">Supprimer</button>
        </div>
      </div>`);
    }
    list.innerHTML = rows.join('');
  };
  await render();

  const w = window as unknown as Record<string, unknown>;
  w['renderCoffrefort'] = () => {
    void render();
  };
  w['deleteCoffreDoc'] = async (id: string) => {
    if (!canMutate) {
      ToastService.warning('Action réservée au gestionnaire RH et à l’administrateur');
      return;
    }
    const ok = await confirmAction({
      host: section,
      title: 'Supprimer le document',
      message: 'Le document sera retiré du coffre-fort. Cette action est définitive.',
      confirmLabel: 'Supprimer',
      danger: true,
    });
    if (!ok) return;
    await CoffreFortStorageService.deleteDocument(ctx, tenantId, id);
    await render();
    ToastService.info('Document supprimé');
  };
  w['handleFileUpload'] = (ev: Event) => {
    if (!canMutate) {
      ToastService.warning('Action réservée au gestionnaire RH et à l’administrateur');
      return;
    }
    const input = ev.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    if (files.length === 0) return;
    const fSalVal = section.querySelector<HTMLSelectElement>('#cf-filter-salarie')?.value ?? '';
    let done = 0;
    const total = files.length;
    for (const file of files) {
      void (async () => {
        try {
          await CoffreFortStorageService.addDocument(ctx, tenantId, file, {
            scope: fSalVal ? 'salarie' : 'entreprise',
            salarie_id: fSalVal || null,
            category: inferCategory(file.name),
          });
          ToastService.success(`« ${file.name} » ajouté`);
        } catch (err) {
          ToastService.error(err instanceof Error ? err.message : String(err));
        } finally {
          done += 1;
          if (done === total) {
            input.value = '';
            await render();
          }
        }
      })();
    }
  };
};
